/**
 * AI-Powered Financial Insights Card
 * Generates rule-based financial insights and recommendations
 * No external API required — works offline and in demo mode
 */
import React, { useMemo } from 'react';
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, Lightbulb } from 'lucide-react';

const defaultFormatCurrency = (val) => `$${(Math.abs(val) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const getValue = (item) => Number(item.currentValue) || 0;
const getGoalTarget = (goal) => Number(goal.target_amount ?? goal.targetAmount) || 0;

const generateInsights = (accounts = { assets: [], liabilities: [] }, goals = [], cashflowMetrics = null, formatCurrencyFn = null) => {
  const formatCurrency = (val) => (formatCurrencyFn || defaultFormatCurrency)(Math.abs(val) || 0);
  const insights = [];
  const assets = accounts.assets || [];
  const liabilities = accounts.liabilities || [];

  const totalAssets = assets.reduce((s, a) => s + getValue(a), 0);
  const totalLiabilities = liabilities.reduce((s, a) => s + getValue(a), 0);
  const netWorth = totalAssets - totalLiabilities;

  // Debt-to-asset ratio
  const debtRatio = totalAssets > 0 ? (totalLiabilities / totalAssets) * 100 : 0;
  if (debtRatio > 80) {
    insights.push({
      id: 'high_debt_ratio',
      type: 'warning',
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      title: 'High Debt Ratio',
      message: `Debt-to-asset ratio is ${debtRatio.toFixed(0)}%. Prioritize debt reduction.`,
      priority: 'high'
    });
  } else if (debtRatio < 30 && totalLiabilities > 0) {
    insights.push({
      id: 'low_debt',
      type: 'positive',
      icon: <TrendingUp className="w-3.5 h-3.5" />,
      title: 'Healthy Debt',
      message: `Debt-to-asset ratio is ${debtRatio.toFixed(0)}%. You’re managing debt well.`,
      priority: 'low'
    });
  }

  // Emergency fund check
  const emergencyFund = assets.find(a => a.name.toLowerCase().includes('emergency'));
  if (!emergencyFund) {
    insights.push({
      id: 'no_emergency',
      type: 'action',
      icon: <Lightbulb className="w-3.5 h-3.5" />,
      title: 'Emergency Fund',
      message: 'Start a 3–6 month cash buffer.',
      priority: 'high'
    });
  } else if (getValue(emergencyFund) < 5000) {
    insights.push({
      id: 'small_emergency',
      type: 'action',
      icon: <Lightbulb className="w-3.5 h-3.5" />,
      title: 'Grow Emergency Fund',
      message: `Emergency fund is ${formatCurrency(getValue(emergencyFund))}; aim for $5,000+ first.`,
      priority: 'medium'
    });
  }

  // Net worth positivity
  if (netWorth > 0) {
    insights.push({
      id: 'positive_nw',
      type: 'positive',
      icon: <TrendingUp className="w-3.5 h-3.5" />,
      title: 'Positive Net Worth',
      message: `${formatCurrency(netWorth)} net worth. Keep building momentum.`,
      priority: 'low'
    });
  } else if (netWorth < 0) {
    insights.push({
      id: 'negative_nw',
      type: 'insight',
      icon: <TrendingDown className="w-3.5 h-3.5" />,
      title: 'Negative Net Worth',
      message: `${formatCurrency(Math.abs(netWorth))} gap. Reduce liabilities first.`,
      priority: 'high'
    });
  }

  // Goal progress
  const goalsInProgress = (goals || []).filter(g => getGoalTarget(g) > 0);
  if (goalsInProgress.length === 0) {
    insights.push({
      id: 'no_goals',
      type: 'action',
      icon: <Lightbulb className="w-3.5 h-3.5" />,
      title: 'Set Goals',
      message: 'Create a clear target to guide monthly decisions.',
      priority: 'medium'
    });
  }

  // Diversification
  if (assets.length === 1) {
    insights.push({
      id: 'concentrated',
      type: 'insight',
      icon: <Sparkles className="w-3.5 h-3.5" />,
      title: 'Diversify Assets',
      message: 'All assets are in one account; spread risk over time.',
      priority: 'medium'
    });
  }

  if (cashflowMetrics?.monthNetCashflow < 0) {
    insights.push({
      id: 'negative_cashflow',
      type: 'warning',
      icon: <TrendingDown className="w-3.5 h-3.5" />,
      title: 'Negative Cashflow',
      message: `This month is down ${formatCurrency(cashflowMetrics.monthNetCashflow)}. Review recurring spend.`,
      priority: 'high'
    });
  } else if (cashflowMetrics?.savingsRate >= 20) {
    insights.push({
      id: 'strong_savings_rate',
      type: 'positive',
      icon: <TrendingUp className="w-3.5 h-3.5" />,
      title: 'Strong Savings',
      message: `${cashflowMetrics.savingsRate.toFixed(0)}% savings rate beats the 20% benchmark.`,
      priority: 'low'
    });
  }

  return insights.sort((a, b) => {
    const p = { high: 0, medium: 1, low: 2 };
    return (p[a.priority] || 2) - (p[b.priority] || 2);
  }).slice(0, 5);
};

const AIInsightsCard = ({ accounts = { assets: [], liabilities: [] }, goals = [], cashflowMetrics = null, formatCurrency = null }) => {
  const insights = useMemo(
    () => generateInsights(accounts, goals, cashflowMetrics, formatCurrency),
    [accounts, goals, cashflowMetrics, formatCurrency]
  );

  const typeStyles = {
    positive: 'border-emerald-200 dark:border-emerald-900/70 bg-emerald-50/70 dark:bg-emerald-950/20',
    warning: 'border-amber-200 dark:border-amber-900/70 bg-amber-50/70 dark:bg-amber-950/20',
    insight: 'border-blue-200 dark:border-blue-900/70 bg-blue-50/70 dark:bg-blue-950/20',
    action: 'border-purple-200 dark:border-purple-900/70 bg-purple-50/70 dark:bg-purple-950/20'
  };

  const iconColors = {
    positive: 'text-emerald-600',
    warning: 'text-amber-600',
    insight: 'text-blue-600',
    action: 'text-purple-600'
  };

  return (
    <div data-testid="ai-insights" className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Smart Insights</h4>
        </div>
        <span className="text-[11px] text-gray-400">{insights.length} active</span>
      </div>

      {insights.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">Add financial data to see personalized insights.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-2">
          {insights.map(insight => (
            <div
              key={insight.id}
              data-testid={`insight-${insight.id}`}
              className={`min-h-[76px] p-2.5 rounded-lg border ${typeStyles[insight.type] || typeStyles.insight}`}
            >
              <div className="flex items-start gap-2">
                <div className={`flex-shrink-0 mt-0.5 ${iconColors[insight.type] || iconColors.insight}`}>
                  {insight.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-tight">{insight.title}</p>
                  <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-1">{insight.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AIInsightsCard;
export { generateInsights };
